
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  connectAuthEmulator,
  initializeAuth,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
} from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import {
  getFirebaseProject as getProjectFromEnv,
  getDatabaseName as getDbNameFromEnv,
  getEnvironmentConfig,
  getLocalhostHostname,
  getLocalhostIP,
  isPrivateLanIPv4Hostname,
  isPreviewChannelHostname,
  type Environment
} from '@/config/environment-config'; // pragma: allowlist secret

// Simple console-based logger to avoid circular dependency issues
// This file cannot import the logger module due to potential circular dependencies
// during module initialization. We use console directly with a similar API.
const consoleLogger = typeof console !== 'undefined' ? console : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
};

const logger = {
  debug: (...args: any[]) => consoleLogger.debug(...args),
  info: (...args: any[]) => consoleLogger.info(...args),
  warn: (...args: any[]) => consoleLogger.warn(...args),
  error: (...args: any[]) => consoleLogger.error(...args),
};

// Type for Firebase configuration
type FirebaseConfigType = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

// Environment configuration type (project IDs come from environment-config)
type EnvironmentConfig = {
  firebaseProject: string;
  databaseName: string;
  authMethod: 'popup' | 'redirect';
};

// Firebase configurations for different projects
// Note: Keys must match actual project IDs, but we use environment config to get the values
const prodEnvConfig = getEnvironmentConfig('prod');
const devEnvConfig = getEnvironmentConfig('dev');

const firebaseConfigs: Record<string, FirebaseConfigType> = {
  // Production project
  [prodEnvConfig.firebaseProject]: {
    apiKey: "AIzaSyAZwstp1MG05VeXbdH23mb_2PZACyei49A",
    authDomain: `${prodEnvConfig.firebaseProject}.firebaseapp.com`,
    projectId: prodEnvConfig.firebaseProject,
    storageBucket: `${prodEnvConfig.firebaseProject}.firebasestorage.app`,
    messagingSenderId: "979524553992",
    appId: "1:979524553992:web:5e7ef58b305afbf14470bd",
    measurementId: "G-KQXGLX9VTH"
  },
  // Development/staging project
  [devEnvConfig.firebaseProject]: {
    apiKey: "AIzaSyAmW4UsOWOErxsKnIbtS4CI_Av4p8Q8kfo",
    authDomain: `${devEnvConfig.firebaseProject}.firebaseapp.com`,
    projectId: devEnvConfig.firebaseProject,
    storageBucket: `${devEnvConfig.firebaseProject}.firebasestorage.app`,
    messagingSenderId: "638787931703",
    appId: "1:638787931703:web:89a7d74ad3887d449379b2"
    // measurementId is not provided for this project
  }
};

// Environment-specific configuration based on hostname
// Uses single source of truth from environment-config.ts for project IDs and database names
function getFirebaseEnvironmentConfig(): EnvironmentConfig {
  if (typeof window === 'undefined') {
    // Server-side rendering - use production defaults
    logger.debug('[🔥 firebase-config] ❌ SERVER-SIDE: Using production defaults (no window object)');
    const prodConfig = getEnvironmentConfig('prod');
    return {
      firebaseProject: prodConfig.firebaseProject,
      databaseName: prodConfig.databaseName,
      authMethod: 'popup'
    };
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  logger.debug('[🔥 firebase-config] ========== ENVIRONMENT DETECTION ==========');
  logger.debug('[🔥 firebase-config] Full URL:', window.location.href);
  logger.debug('[🔥 firebase-config] Hostname:', hostname);
  logger.debug('[🔥 firebase-config] Protocol:', protocol);
  logger.debug('[🔥 firebase-config] Port:', port || 'default');
  const devProject = getProjectFromEnv('dev');
  logger.debug('[🔥 firebase-config] ⚠️ PREVIEW CHANNEL CHECK - hostname starts with dev project prefix?', hostname.startsWith(`${devProject}--`));
  logger.debug('[🔥 firebase-config] ⚠️ PREVIEW CHANNEL CHECK - hostname ends with .web.app?', hostname.endsWith('.web.app'));

  // Determine environment type
  let env: Environment = 'prod';
  let authMethod: 'popup' | 'redirect' = 'popup';

  // Get configs for environment detection
  const devConfig = getEnvironmentConfig('dev');
  const prodConfig = getEnvironmentConfig('prod');

  logger.debug(`[🔥 firebase-config] ⚠️ PREVIEW CHANNEL CHECK - hostname starts with ${devConfig.firebaseProject}--?`, hostname.startsWith(`${devConfig.firebaseProject}--`));
  logger.debug('[🔥 firebase-config] ⚠️ PREVIEW CHANNEL CHECK - hostname ends with .web.app?', hostname.endsWith('.web.app'));

  // LOCALHOST + private LAN: popup on desktop; mobile uses redirect at runtime via shouldUseRedirectAuth()
  const localhostHostname = getLocalhostHostname();
  const localhostIP = getLocalhostIP();
  if (hostname === localhostHostname || hostname === localhostIP) {
    logger.debug('[🔥 firebase-config] ✅ LOCALHOST environment detected');
    env = 'dev';
    authMethod = 'popup';
  } else if (isPrivateLanIPv4Hostname(hostname)) {
    logger.debug('[🔥 firebase-config] ✅ PRIVATE LAN IPv4 detected — using dev playground project');
    env = 'dev';
    authMethod = 'popup';
  }
  // Preview channels: popup. Redirect cannot complete (preview origin != authDomain).
  // shouldUseRedirectAuth() also forces popup on preview, including mobile.
  else if (hostname.startsWith(`${devProject}--`) && hostname.endsWith('.web.app')) {
    logger.debug(`[🔥 firebase-config] ✅ DEVELOPMENT PREVIEW CHANNEL detected (${devProject})`);
    env = 'dev';
    authMethod = 'popup';
  }
  else if (hostname === `${devProject}.web.app` ||
    hostname === `${devProject}.firebaseapp.com`) {
    logger.debug(`[🔥 firebase-config] ✅ DEVELOPMENT environment detected (${devProject})`);
    env = 'dev';
    authMethod = 'redirect';
  }
  // PRODUCTION ENVIRONMENT: prod project domains (from config)
  else {
    const prodProject = getProjectFromEnv('prod');
    if (hostname.startsWith(`${prodProject}--`) && hostname.endsWith('.web.app')) {
      logger.debug(`[🔥 firebase-config] ✅ PRODUCTION PREVIEW CHANNEL detected (${prodProject})`);
      env = 'prod';
      authMethod = 'popup';
    }
    else if (hostname === `${prodProject}.web.app` ||
      hostname === `${prodProject}.firebaseapp.com`) {
      logger.debug(`[🔥 firebase-config] ✅ PRODUCTION environment detected (${prodProject})`);
      env = 'prod';
      authMethod = 'redirect';
    }
    else {
      // Default to production for unknown domains
      logger.debug('[🔥 firebase-config] ⚠️ Unknown domain, defaulting to production config');
      env = 'prod';
      authMethod = 'redirect';
    }
  }

  // Get configuration from single source of truth
  const envConfig = getEnvironmentConfig(env);

  logger.debug('[🔥 firebase-config] Using environment:', env);
  logger.debug('[🔥 firebase-config] Firebase Project:', envConfig.firebaseProject);
  logger.debug('[🔥 firebase-config] Database:', envConfig.databaseName);
  logger.debug('[🔥 firebase-config] Auth Method:', authMethod);

  return {
    firebaseProject: envConfig.firebaseProject,
    databaseName: envConfig.databaseName,
    authMethod
  };
}

// Get environment configuration (uses single source of truth from environment-config.ts)
const envConfig = getFirebaseEnvironmentConfig();

// Get the appropriate Firebase configuration
const firebaseConfig = firebaseConfigs[envConfig.firebaseProject];

if (typeof window !== 'undefined') {
  const hostname = window.location.hostname;
  const projectId = envConfig.firebaseProject;

  const isLocalIPOrLAN = hostname === getLocalhostIP() || isPrivateLanIPv4Hostname(hostname);
  const useEmulator = typeof import.meta !== 'undefined' &&
    (import.meta.env.VITE_USE_EMULATOR === 'true' || import.meta.env.VITE_USE_EMULATOR === true);
  const isHttps = window.location.protocol === 'https:';

  if (isLocalIPOrLAN && !useEmulator && isHttps) {
    firebaseConfig.authDomain = window.location.host;
    logger.debug('[🔥 firebase-config] Using local proxy authDomain for real Firebase on local IP/LAN:', firebaseConfig.authDomain);
  } else {
    // Keep authDomain on the same Firebase Hosting domain for redirect OAuth.
    // Preview channels must use firebaseapp.com; stable web.app / firebaseapp.com hosts use their own domain.
    const isPreviewChannel = isPreviewChannelHostname(hostname);
    logger.debug('[🔥 firebase-config] Setting authDomain for hosted project:', {
      hostname,
      projectId,
      isPreviewChannel,
      currentAuthDomain: firebaseConfig.authDomain,
    });
    if (isPreviewChannel) {
      firebaseConfig.authDomain = `${projectId}.firebaseapp.com`;
      logger.debug('[🔥 firebase-config] ✅ Preview channel detected, setting authDomain to:', firebaseConfig.authDomain);
    } else if (hostname === `${projectId}.web.app`) {
      firebaseConfig.authDomain = `${projectId}.web.app`;
      logger.debug('[🔥 firebase-config] Using web.app as authDomain to stay on same domain');
    } else if (hostname === `${projectId}.firebaseapp.com`) {
      firebaseConfig.authDomain = `${projectId}.firebaseapp.com`;
      logger.debug('[🔥 firebase-config] Using firebaseapp.com as authDomain to stay on same domain');
    }
  }
}

// Log the selected configuration
if (typeof window !== 'undefined') {
  logger.debug('[🔥 firebase-config] ========== FIREBASE CONFIG SELECTED ==========');
  logger.debug('[🔥 firebase-config] Selected Firebase Project:', envConfig.firebaseProject);
  logger.debug('[🔥 firebase-config] Selected Database:', envConfig.databaseName);
  logger.debug('[🔥 firebase-config] Selected Auth Method:', envConfig.authMethod);
  logger.debug('[🔥 firebase-config] Auth Domain:', firebaseConfig.authDomain);
  logger.debug('[🔥 firebase-config] Project ID:', firebaseConfig.projectId);
  logger.debug('[🔥 firebase-config] App ID:', firebaseConfig.appId);
}

// Only initialize Firebase on the client side (not during SSR)
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;

if (typeof window !== 'undefined') {
  logger.debug('[🔥 firebase-config] Initializing Firebase...');
  logger.debug('[🔥 firebase-config] Window location:', window.location.href);
  logger.debug('[🔥 firebase-config] Target configuration:', {
    project: envConfig.firebaseProject,
    authDomain: firebaseConfig.authDomain,
    database: envConfig.databaseName,
    authMethod: envConfig.authMethod
  });

  const existingApps = getApps();
  logger.debug('[🔥 firebase-config] Existing apps:', existingApps.length);

  if (existingApps.length > 0) {
    // Check if the existing app has the correct configuration
    const existingApp = getApp();
    const existingConfig = existingApp.options;
    logger.debug('[🔥 firebase-config] Existing app config:', {
      projectId: existingConfig.projectId,
      authDomain: existingConfig.authDomain
    });

    // If the existing app doesn't match our expected configuration, we'll use it anyway
    // but log a warning (we can't delete and recreate apps in Firebase)
    if (existingConfig.projectId !== firebaseConfig.projectId) {
      logger.warn('[🔥 firebase-config] ⚠️ WARNING: Existing app project mismatch!');
      logger.warn('[🔥 firebase-config] Expected:', firebaseConfig.projectId);
      logger.warn('[🔥 firebase-config] Found:', existingConfig.projectId);
      logger.warn('[🔥 firebase-config] This may cause authentication issues.');
      logger.warn('[🔥 firebase-config] Try refreshing the page to reinitialize.');
    }
    app = existingApp;
  } else {
    app = initializeApp(firebaseConfig);
    logger.debug('[🔥 firebase-config] New Firebase app initialized');
  }

  logger.debug('[🔥 firebase-config] Firebase app configured with:', {
    projectId: app.options.projectId,
    authDomain: app.options.authDomain
  });

  auth = getAuth(app);
  logger.debug('[🔥 firebase-config] Auth instance created');

  // Set persistence to ensure auth state persists across redirects
  logger.debug('[🔥 firebase-config] Setting auth persistence to browserLocalPersistence...');
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      logger.debug('[🔥 firebase-config] ✅ Auth persistence set successfully');
    })
    .catch((error) => {
      logger.error('[🔥 firebase-config] ❌ Error setting auth persistence:', error);
    });

  // Determine if we should use the emulator
  const useEmulator = process.env.NODE_ENV === 'development' &&
    (window.location.hostname === getLocalhostHostname() || window.location.hostname === getLocalhostIP()) &&
    (import.meta.env.VITE_USE_EMULATOR === 'true' || import.meta.env.VITE_USE_EMULATOR === true);

  // Standard initialization for both emulator and prod (mixed mode uses standard WebChannel to real DB)
  db = getFirestore(app, envConfig.databaseName);

  // Expose for debugging in dev
  if (process.env.NODE_ENV === 'development') {
    (window as any)._debugDb = db;
    (window as any)._debugApp = app;
    (window as any)._debugAuth = auth;
    // Also expose auth methods for e2e tests
    import('firebase/auth').then(({ signInWithEmailAndPassword, signInWithCredential, GoogleAuthProvider }) => {
      (window as any)._signInWithEmailAndPassword = signInWithEmailAndPassword;
      (window as any)._signInWithCredential = signInWithCredential;
      (window as any)._GoogleAuthProvider = GoogleAuthProvider;
    });
  }

  logger.debug('[🔥 firebase-config] Firestore initialized with database:', envConfig.databaseName);

  storage = getStorage(app);
  logger.debug('[🔥 firebase-config] Storage initialized');

  const isLocalDevHostname =
    window.location.hostname === getLocalhostHostname() ||
    window.location.hostname === getLocalhostIP() ||
    isPrivateLanIPv4Hostname(window.location.hostname);

  if (process.env.NODE_ENV === 'development' && isLocalDevHostname) {
    logger.debug(`🔑 Using ${envConfig.firebaseProject} Firebase Auth (${envConfig.authMethod} mode for ${getLocalhostHostname()})`);

    if (useEmulator) {
      logger.debug('🔧 Firebase Emulator Suite enabled via VITE_USE_EMULATOR');

      // Connect to Auth emulator for local development testing.
      // Use the same host as the page (localhost vs 127.0.0.1) so redirect OAuth state is preserved.
      try {
        if (!(auth as any).emulatorConfig) {
          const authEmulatorHost =
            window.location.hostname === getLocalhostHostname()
              ? getLocalhostHostname()
              : getLocalhostIP();
          const emulatorUrl = `http://${authEmulatorHost}:9099`;
          connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true });
          logger.debug(`🔐 Connected to Firebase Auth Emulator at ${emulatorUrl}`);
        }
      } catch (error) {
        logger.debug('🔐 Auth Emulator connection skipped (emulator may not be running):', error instanceof Error ? error.message : String(error));
      }

      // Connect to Firestore Emulator
      try {
        const localhostIP = getLocalhostIP();
        connectFirestoreEmulator(db, localhostIP, 8081);
        logger.debug(`🔥 Connected to Firebase Firestore Emulator at http://${localhostIP}:8081`);
      } catch (error) {
        logger.debug('🔥 Firestore Emulator connection skipped:', error instanceof Error ? error.message : String(error));
      }

      // Connect to Storage emulator for localhost development
      try {
        const localhostIP = getLocalhostIP();
        connectStorageEmulator(storage, localhostIP, 9199);
        logger.debug(`💾 Connected to Firebase Storage Emulator at http://${localhostIP}:9199`);
      } catch (error) {
        logger.debug('💾 Storage Emulator connection skipped:', error instanceof Error ? error.message : String(error));
      }
    } else {
      logger.debug('🔑 Firebase Emulator Suite DISABLED (set VITE_USE_EMULATOR=true to enable)');
      logger.debug(`📡 Using real Firebase project ${envConfig.firebaseProject} (emulators not enabled)`);
      logger.debug(
        `⚠️  Note: Storage uploads from this origin may fail due to CORS. Enable emulator or configure CORS in Firebase Console.`
      );
    }
  }
}

// This function ensures Firestore is only initialized on the client side.
const getDb = () => {
  if (typeof window === 'undefined') {
    throw new Error('Firestore can only be accessed on the client side');
  }
  return db!;
};

// Export the environment configuration for use in other parts of the app
const getAuthMethod = () => envConfig.authMethod;
const getFirebaseProject = () => envConfig.firebaseProject;
const getDatabaseName = () => envConfig.databaseName;
const isAuthEmulatorActive = () =>
  typeof window !== 'undefined' &&
  !!auth &&
  !!(auth as Auth & { emulatorConfig?: unknown }).emulatorConfig;

const SIGN_IN_LINK_SENDER_APP_NAME = 'signInLinkSender';

function authEmulatorUrl(instance: Auth | null): string | null {
  const config = (instance as Auth & {
    emulatorConfig?: { host?: string; port?: number | null; protocol?: string; url?: string };
  } | null)?.emulatorConfig;
  if (!config) {
    return null;
  }
  if (config.url) {
    return config.url;
  }
  if (config.host) {
    const port = config.port ? `:${config.port}` : '';
    return `${config.protocol || 'http'}://${config.host}${port}`;
  }
  return null;
}

/**
 * Unauthenticated Auth used only to send invitee sign-in emails.
 * The signed-in administrator session can swallow the first Firebase mail
 * (sendSignInLinkToEmail resolves, inbox stays empty).
 */
function getSignInLinkSenderAuth(): Auth | null {
  if (typeof window === 'undefined' || !app) {
    return null;
  }
  const existing = getApps().find((candidate) => candidate.name === SIGN_IN_LINK_SENDER_APP_NAME);
  const senderApp = existing ?? initializeApp(app.options, SIGN_IN_LINK_SENDER_APP_NAME);
  let senderAuth: Auth;
  try {
    senderAuth = initializeAuth(senderApp, { persistence: inMemoryPersistence });
  } catch {
    senderAuth = getAuth(senderApp);
  }
  const emulatorUrl = authEmulatorUrl(auth);
  if (emulatorUrl && !authEmulatorUrl(senderAuth)) {
    try {
      connectAuthEmulator(senderAuth, emulatorUrl, { disableWarnings: true });
    } catch {
      // Already connected on this Auth instance.
    }
  }
  return senderAuth;
}

/** True when Google sign-in should use signInWithRedirect instead of popup. */
const shouldUseRedirectAuth = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const forceMethod = urlParams.get('authMethod');
    if (forceMethod === 'redirect') {
      return true;
    }
    if (forceMethod === 'popup') {
      return false;
    }
  }

  if (typeof window === 'undefined') {
    return false;
  }

  if (isAuthEmulatorActive()) {
    return true;
  }
  const hostname = window.location.hostname;
  const localhostHostname = getLocalhostHostname();
  const localhostIP = getLocalhostIP();
  const isLocalHost = hostname === localhostHostname || hostname === localhostIP || isPrivateLanIPv4Hostname(hostname);

  // Preview channels: popup. Redirect stores the OAuth result on authDomain
  // (project.firebaseapp.com), a different origin from `{project}--pr*.web.app`,
  // so getRedirectResult() returns null under third-party storage partitioning.
  if (isPreviewChannelHostname(hostname)) {
    return false;
  }

  // Local dev against real Firebase: popup keeps OAuth state on localhost.
  if (isLocalHost) {
    return false;
  }

  // Mobile always uses redirect for reliability (popup is blocked or broken).
  const ua = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line|WeChat|Snapchat/i.test(ua);
  if (isMobile || isInAppBrowser) {
    return true;
  }

  if (getAuthMethod() === 'redirect') {
    return true;
  }

  return false;
};

/** Initializes and returns Firebase Performance instance on client side. */
const getPerformanceInstance = async () => {
  if (typeof window === 'undefined' || !app) {
    return null;
  }
  try {
    const { getPerformance } = await import('firebase/performance');
    return getPerformance(app);
  } catch (err) {
    logger.debug('[🔥 firebase-config] Performance Monitoring unavailable or disabled:', err);
    return null;
  }
};

export {
  app,
  auth,
  getDb,
  storage,
  getPerformanceInstance,
  getAuthMethod,
  getFirebaseProject,
  getDatabaseName,
  isAuthEmulatorActive,
  shouldUseRedirectAuth,
  getSignInLinkSenderAuth,
};

